import { type SugarEnrollmentItem, type SugarOptionName } from '@app/types'
import { deburr, orderBy } from 'lodash'

import {
  APPLICATION_VIEWS,
  applicationStatusOf,
  SCHOLARSHIP_DENIED_STATUSES,
  SCHOLARSHIP_PENDING_STATUSES
} from './funding'

export type RosterSortColumn = 'name' | 'participationType' | 'enrollmentStatus' | 'applicationStatus' | 'scholarship'
export type RosterSortDirection = 'asc' | 'desc'
export type RosterSortState = {
  column: RosterSortColumn
  direction: RosterSortDirection
}

// Paired with the index into the unfiltered roster, because the funding form
// binds its rows by position. Sorting or filtering these pairs reorders what is
// rendered without touching the array the form is bound to.
export type RosterRow = {
  item: SugarEnrollmentItem
  itemIdx: number
}

// Participation Type and Enrollment Status are stored as wire values and
// rendered through Sugar's option lists, so both sort on the resolved label.
// The roster payload carries no statusDisplay/participationTypeDisplay despite
// the type declaring them — reading those would sort every row on undefined.
export type RosterOptionLabel = (option: SugarOptionName, value: string) => string | undefined

export const DEFAULT_ROSTER_SORT: RosterSortState = {
  column: 'name',
  direction: 'asc'
}

// Accent- and case-folding shared by sorting and search, so a provider who
// types "nunez" both finds and reads "Núñez" where they expect it. lodash
// deburr covers Latin-1 Supplement and Latin Extended-A/B; other scripts fall
// through to code point order, which Intl.Collator('en') would not improve on.
export const normalizeRosterText = (value?: string | null): string => deburr(value ?? '').toLowerCase()

// Scholarship has no meaningful alphabetical order, so it ranks by the same
// approved -> pending -> denied sequence the status views used to impose. Stated
// explicitly rather than derived from the view list: the views now partition on
// Application Status (MIQ-2660 QA), so deriving this from them would test
// scholarship values against application predicates and rank every row past the
// end -- silently, and without a type error. Membership is unchanged.
const SCHOLARSHIP_RANKS: readonly ((status: string) => boolean)[] = [
  (status) => status === 'active',
  (status) => SCHOLARSHIP_PENDING_STATUSES.includes(status),
  (status) => SCHOLARSHIP_DENIED_STATUSES.includes(status)
]

// A status in no bucket -- provider_not_participating, inactive, or null --
// ranks past the end, so ascending order puts them after every bucketed row. The
// rank is a plain ordinal rather than an empty value, so it does not reach
// sortRosterRows' "empty sorts last in both directions" guard, and a descending
// sort leads with them. That is the pre-existing behaviour, preserved, and the
// acceptance criterion covers ascending only.
const scholarshipRank = (status: string | null): number => {
  if (status === null) {
    return SCHOLARSHIP_RANKS.length
  }

  const rank = SCHOLARSHIP_RANKS.findIndex((match) => match(status))

  return rank === -1 ? SCHOLARSHIP_RANKS.length : rank
}

// Derived from the view list, so the Application Status column and the tabs
// above it cannot drift -- the invariant the scholarship rank used to carry,
// re-pointed at the pair that now has both a column and tabs. 'all' is dropped
// because it matches every row and would rank them all zero.
const APPLICATION_BUCKETS = APPLICATION_VIEWS.filter(({ value }) => value !== 'all')

// Same past-the-end treatment as scholarship, and the same caveat: a blank or
// absent application status ranks after every bucketed row, so it sorts last
// ASCENDING only. The rank is a plain ordinal rather than an empty value, so it
// never reaches sortRosterRows' "empty sorts last in both directions" guard --
// which fires on '' alone -- and a descending sort leads with the blanks. That
// is deliberate: the Scholarship column two positions right behaves identically
// (see SCHOLARSHIP_RANKS above), and these two columns sit close enough that a
// provider will compare them. Divergence here would cost more than the quirk.
const applicationRank = (status: string | null): number => {
  const rank = APPLICATION_BUCKETS.findIndex(({ match }) => match(status))

  return rank === -1 ? APPLICATION_BUCKETS.length : rank
}

// Returns a normalized string for the three text columns and a rank for the
// application status and scholarship columns. Callers must not normalize the
// result again — the rank is a number, and deburr would coerce it.
export const rosterSortKey = (
  column: RosterSortColumn,
  item: SugarEnrollmentItem,
  getOptionLabel: RosterOptionLabel
): number | string => {
  switch (column) {
    case 'enrollmentStatus':
      return normalizeRosterText(getOptionLabel('enrollmentStatus', item.enrollment.status) ?? item.enrollment.status)
    case 'participationType':
      return normalizeRosterText(
        getOptionLabel('participationTypes', item.enrollment.participationType) ?? item.enrollment.participationType
      )
    case 'applicationStatus':
      return applicationRank(applicationStatusOf(item))
    case 'scholarship':
      return scholarshipRank(item.enrollment.gapStatus)
    case 'name':
    default:
      // contact.name arrives as "Last, First", so a plain compare is last-name
      // order and matches what the Name column displays.
      return normalizeRosterText(item.contact.name)
  }
}

export const sortRosterRows = (
  rows: RosterRow[],
  sort: RosterSortState,
  getOptionLabel: RosterOptionLabel
): RosterRow[] =>
  orderBy(
    rows,
    [
      // Rows with no value for the active column sort last in both directions,
      // rather than clumping at whichever end the direction happens to point.
      ({ item }) => (rosterSortKey(sort.column, item, getOptionLabel) === '' ? 1 : 0),
      ({ item }) => rosterSortKey(sort.column, item, getOptionLabel),
      // Every sortable column is non-unique, so equal keys need a unique
      // tiebreak or their relative order can drift between renders.
      ({ item }) => item.enrollment.id
    ],
    ['asc', sort.direction, 'asc']
  )

// Every whitespace-separated term must appear, so "smith ja" finds
// "Smith, Jaiden". Matches the student name only.
export const matchesRosterSearch = (item: SugarEnrollmentItem, search: string): boolean => {
  const terms = normalizeRosterText(search).split(/\s+/).filter(Boolean)

  if (!terms.length) {
    return true
  }

  const haystack = normalizeRosterText(item.contact.name)

  return terms.every((term) => haystack.includes(term))
}

// Clicking the active column flips direction; clicking any other starts it
// ascending, so a provider never lands in a descending sort they did not ask for.
export const nextRosterSort = (current: RosterSortState, column: RosterSortColumn): RosterSortState =>
  current.column === column
    ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { column, direction: 'asc' }

export const rosterAriaSort = (
  current: RosterSortState,
  column: RosterSortColumn
): 'ascending' | 'descending' | 'none' => {
  if (current.column !== column) {
    return 'none'
  }

  return current.direction === 'asc' ? 'ascending' : 'descending'
}
